// The consent flow in the browser (02-DESIGN/08-toolkits/03-linking.md § the
// flow): the authorization-code flow with PKCE against the toolkit's own
// registered client, with the console's page for that toolkit as the
// redirect, the state and the verifier kept in this tab until the provider
// returns, and the code read back off the page's query. One registration
// per toolkit per install carries that page as its redirect URI, which is
// the onboarding step the add form documents.

import { followable, tenantPath } from "../../address/address.ts";
import type { Store } from "../../signin/tokens.ts";
import { authorizationUrl, newState, pkce, type Toolkit } from "../../wire/toolkit.ts";

/** PendingLink is a link under way: what the callback needs to finish it. */
export interface PendingLink {
  state: string;
  verifier: string;
  /** The toolkit's name and the workload it is, so that the callback checks it still is. */
  toolkit: string;
  workload: string;
  redirectUri: string;
  begun: number;
}

/** LINK_LIFE bounds how long a link under way is kept, in milliseconds. */
export const LINK_LIFE = 15 * 60 * 1000;

const KEY = "impire.console.link";

function storage(): Store | undefined {
  try {
    return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

/** redirectUriFor is the console's page for the toolkit: the redirect URI its registration at the provider carries. */
export function redirectUriFor(origin: string, slug: string, name: string): string {
  return origin + tenantPath(slug, "toolkits", name);
}

/** savePendingLink keeps a link under way in this tab, replacing any other. */
export function savePendingLink(p: PendingLink, store: Store | undefined = storage()): void {
  store?.setItem(KEY, JSON.stringify(p));
}

/** takePendingLink takes the link under way by its state, once, within its life. */
export function takePendingLink(
  state: string,
  store: Store | undefined = storage(),
): PendingLink | undefined {
  const raw = store?.getItem(KEY);
  if (raw === null || raw === undefined) {
    return undefined;
  }
  store?.removeItem(KEY);
  let p: unknown;
  try {
    p = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof p !== "object" || p === null) {
    return undefined;
  }
  const link = p as PendingLink;
  if (link.state !== state || typeof link.verifier !== "string" || Date.now() - link.begun > LINK_LIFE) {
    return undefined;
  }
  return link;
}

/**
 * beginLink mints the PKCE pair and the state, keeps them, and answers the
 * authorization URL to send the browser to — only an https one, whatever the
 * toolkit advertised.
 */
export async function beginLink(
  origin: string,
  slug: string,
  tk: Toolkit,
  store: Store | undefined = storage(),
): Promise<string> {
  const { verifier, challenge } = await pkce();
  const state = newState();
  const redirectUri = redirectUriFor(origin, slug, tk.name);
  const url = authorizationUrl(tk, { redirectUri, state, challenge });
  if (!followable(url)) {
    throw new Error(`toolkit ${tk.name} advertises an authorize endpoint that is not https`);
  }
  savePendingLink(
    { state, verifier, toolkit: tk.name, workload: tk.workload, redirectUri, begun: Date.now() },
    store,
  );
  return url;
}

/** Callback is what the provider returned to the toolkit's page: a code, or a refusal. */
export type Callback = { kind: "code"; code: string; state: string } | { kind: "refused"; reason: string };

/** callbackOf reads the provider's return off the page's query; undefined when the page was opened plainly. */
export function callbackOf(search: string): Callback | undefined {
  const q = new URLSearchParams(search);
  const error = q.get("error");
  if (error !== null) {
    const description = q.get("error_description");
    return { kind: "refused", reason: description === null || description === "" ? error : description };
  }
  const code = q.get("code");
  const state = q.get("state");
  if (code === null || state === null || code === "" || state === "") {
    return undefined;
  }
  return { kind: "code", code, state };
}
